import React, {
  Fragment,
  useEffect,
  useState,
  useCallback,
  useRef,
} from "react";
import { Trans, useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";

import { useDispatch } from "react-redux";
import { findFederated } from "../../actions/findFederatedAction";
import {
  getSAMLContext,
  useNoSessionGuard,
} from "../SigninControllers/signin-controllers";
import {
  CoreButton,
  CoreNotification,
  CoreTextfield,
} from "@komodo-design-system/komodo-components-react";
import "./signin-identifier.scss";
import CookieService from "../../services/cookie.service";
import { findRpUrls } from "../../actions/rpUrlsAction";
import { clearAuthStorage } from "../../utils/utils";
import config from "../../constants/config";
import useRedirectUrl from "../../hooks/rpDetails";
import CommonSigninService from "../../services/common-signin.service";
import { configureDigitalDataOnError } from "../../utils/adobeAnalytics.utils";
import {
  processServiceUrls,
  storeRpUrls,
  isCloudFlow,
} from "../../utils/serviceUrls.utils";
import { getCountry } from "../../services/localeSupport.service";
import { isAccountCreationHiddenForClient } from "../../utils/rpDetails.utils";

const SigninIdentifier = () => {
  useNoSessionGuard(!window.location.href.includes("cloud"));
  const [t] = useTranslation("global");
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const rpdetailsUrl = useRedirectUrl();
  const hideAccountCreationForClient = isAccountCreationHiddenForClient(
    rpdetailsUrl?.client_id || CookieService.getCookie("rp_clientid"),
  );
  const hideCommerceSidePanel = rpdetailsUrl?.hideCommerceSidePanel === true;
  const usernameInputRef = useRef(null);
  const nextButtonRef = useRef(null);
  const handleUsernameSubmitRef = useRef(null);
  const [areStencilControlsReady, setAreStencilControlsReady] =
    useState(false);
  const [username, setUsername] = useState("");
  const [rpUrls, setRpUrls] = useState(null);
  const [cipRu] = useState(() => {
    const cip_ru = CookieService.getCookie("cip_ru");
    return cip_ru === "true" || cip_ru === "email";
  });
  const [userErrorMessage, setUserErrorMessage] = useState(null);
  const [isTouched, setIsTouched] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [errorToast, setErrorToast] = useState(false);
  const [isNotChinaDomain, setIsNotChinaDomain] = useState(false);
  const [loyaltyFormFlag] = useState(
    () => CookieService.getCookie("aspireFormSignIn"),
  );
  const [registrationType] = useState(
    () => new URLSearchParams(window.location.search).get("regType"),
  );
  const [createAccountUrl, setCreateAccountUrl] = useState(
    "/global-registration/registration",
  );
  const [resetPwdUrl, setResetPwdUrl] = useState(
    "/identity/account/reset-password/v2/request",
  );
  const chinaMobileSigninUrl = `${
    process.env["REACT_APP_SERVICE_URL" + config.ENV_SUFIX]
  }/identity/mobile/signin`;

  // Get SSI content from context

  const setServiceUrls = useCallback(
    (response) => {
      const result = processServiceUrls(response, {
        loyaltyFormFlag,
        registrationType,
      });

      setRpUrls(result.rpUrls);

      // Only update URLs if not cloud flow
      if (!isCloudFlow()) {
        setCreateAccountUrl(result.createAccountUrl);
        setResetPwdUrl(result.resetPwdUrl);
      }
    },
    [loyaltyFormFlag, registrationType],
  );

  const fetchRPServiceUrls = useCallback(async () => {
    try {
      const response = await dispatch(findRpUrls());
      if (Array.isArray(response)) {
        storeRpUrls(response);
        const storedRpUrl = sessionStorage.getItem("rp_url");

        if (response.length > 1) {
          setServiceUrls(response);
        } else if (storedRpUrl) {
          setServiceUrls(JSON.parse(storedRpUrl));
        } else {
          setServiceUrls([]);
        }
      }
    } catch (error) {
      console.error("Error fetching RP URLs:", error);
    }
  }, [dispatch, setServiceUrls]);

  const redirectInSameTab = (url) => {
    CommonSigninService.clearSigninSessionStorage();
    CommonSigninService.clearIdentifierCookie();
    window.location.assign(url);
  };

  const handleUsernameSubmit = async (e) => {
    // e.preventDefault();

    setIsTouched(true);
    // Validate username using common service
    const validation = CommonSigninService.validateUsername(username);
    if (!validation.isValid) {
      window.tfIdentityGlobals?.hideLoader();
      setUserErrorMessage("errors.EMPTY_USERNAME");
      configureDigitalDataOnError(validation.error);
      return;
    }
    window.tfIdentityGlobals?.hideLoader();

    try {
      // Set cookies and session storage using common service
      CommonSigninService.setSigninCookies(username.trim());
      CommonSigninService.setSigninSessionStorage(username.trim());
      let pathName = window.location.pathname.split("/")[2];

      const fedPayload = CommonSigninService.createFederatedPayload(
        pathName,
        username.trim(),
      );

      let redirectUrl = CommonSigninService.redirectUrl(
        pathName,
        username.trim(),
      )?.redirectUrl;
      // Check if the user is in China and handle Lite Registration
      if (CommonSigninService.isChinaCountry()) {
        await CommonSigninService.handleLiteRegRedirect(
          username.trim(),
          rpUrls,
          redirectUrl,
          navigate,
        );
      } else {
        const response = await dispatch(findFederated(fedPayload));

        // Handle federated user redirection

        await CommonSigninService.handleFederatedRedirection(
          response,
          username.trim(),
          rpUrls,
          redirectUrl,
          navigate,
        );
        window.tfIdentityGlobals?.hideLoader();
        return;
      }
    } catch (error) {
      window.tfIdentityGlobals?.hideLoader();
      console.error("Error in findFederated:", error);
      setErrorToast(true);
    }
  };

  // Always keep ref in sync with latest handleUsernameSubmit
  handleUsernameSubmitRef.current = handleUsernameSubmit;

  ///////////////////////////////////////////////// Use Effect Block /////////////////////////////////////////////////
  useEffect(() => {
    // Initialize authentication context only once
    const initializeAuthContext = () => {
      // Conditionally call context function based on location path
      if (window.location.href.includes("cloud")) {
        getSAMLContext();
      }
      // OIDC session guard is handled by useNoSessionGuard â€” no call needed here
    };

    // Only initialize context on component mount
    initializeAuthContext();
    clearAuthStorage();
  }, []); // Empty dependency array - run only once on mount

  useEffect(() => {
    // Initialize form when dependencies are ready
    if (loyaltyFormFlag !== null || registrationType !== null) {

      if (
        !isCloudFlow() &&
        (loyaltyFormFlag !== "1" || registrationType !== "FULL")
      ) {
        fetchRPServiceUrls();
      }
    }
  }, [fetchRPServiceUrls, loyaltyFormFlag, registrationType]); // Run when these values change

  // Stop overlay once rpDetails values are retrieved
  useEffect(() => {
    if (rpdetailsUrl && typeof rpdetailsUrl === "object") {
      // Update header logo link
      window.tfIdentityGlobals?.setHeaderLogLink?.(
        rpdetailsUrl?.logoLink,
        true,
      );
      window.tfIdentityGlobals.setFooterLinks({
        privacy: rpdetailsUrl?.privacy_policy,
      });
      window?.tfIdentityGlobals?.hideLoader();
      if (
        getCountry().toLowerCase() === "cn" &&
        rpdetailsUrl?.logoLink?.indexOf(".thermofisher.com.cn") !== -1
      ) {
        setIsNotChinaDomain(true);
      } else {
        setIsNotChinaDomain(false);
      }
    }
  }, [rpdetailsUrl]);

  useEffect(() => {
    // Retrieve stored username from previous page and focus input
    // Remember user on back button from signin.html
    const shouldRestoreUsername =
      location.state?.fromSignin ||
      sessionStorage.getItem("signin_identifier_redirect") === "true" ||
      document.referrer.indexOf("signin.html") > 0;

    if (shouldRestoreUsername) {
      const cookieValue = CookieService.getCookie("cip_identifier");
      if (cookieValue) {
        setUsername(cookieValue);
      }
      sessionStorage.removeItem("signin_identifier_redirect");
    }

    let cleanupKeyDown = null;

    // Poll until shadow DOM is ready (retries every 100ms, up to 2s)
    let attempts = 0;
    const maxAttempts = 20;
    const interval = setInterval(() => {
      attempts++;
      const input = usernameInputRef.current?.shadowRoot?.querySelector(
        ".core-textfield__input",
      );
      const button =
        nextButtonRef.current?.shadowRoot?.querySelector(".core-button");

      if (input) {
        input.id = "username-field";

        // Attach keydown listener for Enter key and other keys
        const handleKeyDown = (e) => {
          if (e.key === "Enter") {
            window.tfIdentityGlobals?.hideLoader();
            sessionStorage.setItem("signin_username", e?.target?.value?.trim());
            handleUsernameSubmitRef.current();
          }
        };
        input.addEventListener("keydown", handleKeyDown);
        cleanupKeyDown = () =>
          input.removeEventListener("keydown", handleKeyDown);
      }

      if (button) {
        button.id = "next-button";
      }

      if (input && button) {
        setAreStencilControlsReady(true);
        clearInterval(interval);
      } else if (attempts >= maxAttempts) {
        setAreStencilControlsReady(true);
        clearInterval(interval);
      }
    }, 100);

    return () => {
      clearInterval(interval);
      if (cleanupKeyDown) cleanupKeyDown();
    };
  }, []); // Run once on mount

  const isReady =
    rpdetailsUrl &&
    typeof rpdetailsUrl === "object" &&
    areStencilControlsReady;

  useEffect(() => {
    if (!isReady) return undefined;

    const focusTimer = setTimeout(() => {
      usernameInputRef.current?.shadowRoot
        ?.querySelector(".core-textfield__input")
        ?.focus();
    }, 0);

    return () => clearTimeout(focusTimer);
  }, [isReady]);

  return (
    <Fragment>
      <div className={`loginForm__main signin-identifier-page${isReady ? " signin-identifier-page--ready" : ""}`}>
          <div
            className={`loginForm ${cipRu || hideCommerceSidePanel || isNotChinaDomain || hideAccountCreationForClient ? "cipRu-specific-class" : ""}`}
          >
            <div className="loginForm__left">
              <p className="loginForm__left__signin-title">
                {t("translations.SIGN_INTO_LABEL")}
              </p>
              {errorToast && (
                <CoreNotification
                  className="notification"
                  label="Error: "
                  message="Username validation failed, please try again."
                  variant="toast"
                  state="error"
                  nocloseicon={true}
                />
              )}
              <div className="loginForm__left__form">
                <CoreTextfield
                  ref={usernameInputRef}
                  className="loginForm__left__username"
                  data-testid="username-field"
                  type="text"
                  name="loginID"
                  autocomplete={true}
                  label={
                    getCountry() === "cn"
                      ? t("translations.LOGINID_1311311543682226_LABEL")
                      : t("translations.EMAILID_1311311543682226_LABEL")
                  }
                  required={getCountry().toLowerCase() === "cn"}
                  fullWidth
                  value={username}
                  invalid={isTouched && !!userErrorMessage}
                  helper={
                    isTouched && userErrorMessage
                      ? t(userErrorMessage)
                      : undefined
                  }
                  onTextfieldChange={(e) => {
                    const value = (e?.detail ?? e?.target?.value ?? "").replace(
                      /\s/g,
                      "",
                    );
                    if (e?.target) {
                      e.target.value = value;
                    }
                    if (usernameInputRef.current) {
                      usernameInputRef.current.value = value;
                    }
                    const input = usernameInputRef.current?.shadowRoot?.querySelector(
                      ".core-textfield__input",
                    );
                    if (input) {
                      input.value = value;
                    }
                    CookieService.setCookie(
                      "cip_identifier",
                      value,
                      process.env[`REACT_APP_COOKIE_DOMAIN${config.ENV_SUFIX}`],
                      60,
                    );
                    setUsername(value);
                    setIsDirty(true);
                    if (isTouched) {
                      // Only validate if already touched
                      if (!value) {
                        setUserErrorMessage("errors.EMPTY_USERNAME");
                      } else {
                        setUserErrorMessage("");
                      }
                    }
                  }}
                  onTextfieldFocusOut={() => {
                    setIsTouched(true);
                    if (isDirty && !username.trim()) {
                      setUserErrorMessage("errors.EMPTY_USERNAME");
                    }
                  }}
                />

                <div>
                  <CoreButton
                    ref={nextButtonRef}
                    type="button"
                    variant="primary"
                    size="large"
                    fullspace
                    onClick={handleUsernameSubmit}
                  >
                    <span slot="content">{t("translations.NEXT_LABEL")}</span>
                  </CoreButton>
                  <p className="loginForm__left__form__tip_tool">
                    <a
                      className="loginForm__left__form__tip_tool__trouble-signin"
                      id="forgotPswd"
                      href={resetPwdUrl}
                      target="_self"
                      tabIndex={0}
                      style={{ cursor: "pointer" }}
                      onContextMenu={(e) => e.preventDefault()}
                      onClick={(e) => {
                        e.preventDefault();
                        redirectInSameTab(resetPwdUrl);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          redirectInSameTab(resetPwdUrl);
                        }
                      }}
                    >
                      {t("translations.LINK_93845637669062720_LABEL")}
                    </a>
                  </p>
                  {getCountry().toLowerCase() === "cn" && (
                    <p className="loginForm__left__form__tip_tool">
                      <a
                        className="loginForm__left__form__tip_tool__trouble-signin"
                        id="forgotPswd"
                        href={chinaMobileSigninUrl}
                        target="_self"
                        tabIndex={0}
                        style={{ cursor: "pointer" }}
                        onContextMenu={(e) => e.preventDefault()}
                        onClick={(e) => {
                          e.preventDefault();
                          redirectInSameTab(chinaMobileSigninUrl);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            redirectInSameTab(chinaMobileSigninUrl);
                          }
                        }}
                      >
                        {t("translations.LINK_MOBILE_LABEL")}
                      </a>
                    </p>
                  )}
                </div>
              </div>
            </div>
            <div
              className={`loginForm__right ${cipRu || hideCommerceSidePanel || isNotChinaDomain || hideAccountCreationForClient ? "cipRu-specific-class" : ""} ${hideCommerceSidePanel ? "loginForm__right--assistance-only" : ""}`}
            >
              {!isNotChinaDomain && !hideAccountCreationForClient ? (
                <>
                  {!cipRu && !hideCommerceSidePanel && (
                    <>
                      <p className="loginForm__right__signin-title">
                        {t("registerSideScreen.infoTitle1.title")}
                      </p>
                      <p className="loginForm__right__heading">
                        <Trans
                          t={t}
                          i18nKey="registerSideScreen.infoTitle1.heading"
                        />
                      </p>
                      {getCountry().toLowerCase() !== "cn" && (
                        <ul>
                          <li>
                            <Trans
                              t={t}
                              i18nKey="registerSideScreen.infoTitle1.line1"
                            />
                          </li>
                          <li>{t("registerSideScreen.infoTitle1.line2")}</li>
                          <li>{t("registerSideScreen.infoTitle1.line3")}</li>
                          {(getCountry().toLowerCase() === "us" ||
                            getCountry().toLowerCase() === "ca") && (
                            <li>{t("registerSideScreen.infoTitle1.line4")}</li>
                          )}
                        </ul>
                      )}
                    </>
                  )}
                  {cipRu && !hideCommerceSidePanel && (
                    <div className="loginForm__right__divider">
                      <span className="loginForm__right__divider__line"></span>
                      <span className="loginForm__right__divider__text">
                        {t("registerSideScreen.infoTitle1.title")}
                      </span>
                      <span className="loginForm__right__divider__line"></span>
                    </div>
                  )}
                  {!hideCommerceSidePanel && (
                    <div className="loginForm__right__create-account">
                      <CoreButton
                        type="button"
                        variant="outline"
                        size="large"
                        href={createAccountUrl}
                        fullspace
                      >
                        <span slot="content">
                          {t("registerSideScreen.labels.createAcc")}
                        </span>
                      </CoreButton>
                    </div>
                  )}
                  {hideCommerceSidePanel && (
                    <p className="loginForm__right__tip_tool">
                      <p>
                        {t("needAssistance.needAssistance")}{" "}
                        <a
                          href={
                            rpdetailsUrl?.contactUsLink
                              ? rpdetailsUrl?.contactUsLink
                              : rpdetailsUrl?.siteUrl +
                                config.environment.pages.contact_us
                          }
                        >
                          {t("needAssistance.contactUs")}
                        </a>
                      </p>
                    </p>
                  )}
                  {!cipRu && !hideCommerceSidePanel && (
                    <p className="loginForm__right__tip_tool">
                      <p>
                        {t("registerSideScreen.labels.questions")}{" "}
                        <a
                          href={
                            rpdetailsUrl?.contactUsLink
                              ? rpdetailsUrl?.contactUsLink
                              : rpdetailsUrl?.siteUrl +
                                config.environment.pages.contact_us
                          }
                        >
                          {t("registerSideScreen.labels.contactUs")}
                        </a>
                      </p>
                    </p>
                  )}
                </>
              ) : (
                <p className="loginForm__right__tip_tool">
                  <p>
                    {hideCommerceSidePanel
                      ? t("needAssistance.needAssistance")
                      : t("registerSideScreen.labels.questions")}{" "}
                    <a
                      href={
                        rpdetailsUrl?.contactUsLink
                          ? rpdetailsUrl?.contactUsLink
                          : rpdetailsUrl?.siteUrl +
                            config.environment.pages.contact_us
                      }
                    >
                      {hideCommerceSidePanel
                        ? t("needAssistance.contactUs")
                        : t("registerSideScreen.labels.contactUs")}
                    </a>
                  </p>
                </p>
              )}
            </div>
          </div>
        </div>
      {/*       {footerHtml && <div dangerouslySetInnerHTML={{ __html: footerHtml }} />}
       */}
      {/* <Footer /> */}
    </Fragment>
  );
};

export default SigninIdentifier;
